// Frise chronologique : drag libre (avec inertie) mêlé à une navigation par étape.
// Les deux flèches sont gérées par le module navigation de Swiper : la classe
// swiper-button-disabled les masque (cf. _block-timeline.scss), donc la flèche retour
// n'apparaît qu'une fois la frise entamée, et la flèche avant disparaît à la fin.
export default class BlockTimeline {
	constructor() {
		const blocks = document.querySelectorAll(".block-timeline");
		if (!blocks.length) return;

		blocks.forEach((block) => {
			const swiperEl = block.querySelector(".swiper");
			if (!swiperEl) return;

			const prevEl = block.querySelector(".block-timeline-nav-prev");
			const nextEl = block.querySelector(".block-timeline-nav-next");
			const progressEl = block.querySelector(".block-timeline-line-progress");

			// La rangée au-dessus de l'axe a une hauteur fixe — c'est ce qui garde l'axe, les
			// points et les flèches alignés d'une étape à l'autre. On la cale sur l'étape haute
			// la plus longue : en dur, elle serait soit trop courte (contenu qui déborde sur
			// l'intro), soit trop grande (grand vide au-dessus de l'axe). Le SCSS y ajoute
			// l'écart minimal entre le contenu et l'axe.
			const aboveContents = [...block.querySelectorAll(".block-timeline-step-above .block-timeline-step-content")];
			const alternating = window.matchMedia("(min-width: 768px)");

			const setHalf = () => {
				if (!alternating.matches || !aboveContents.length) {
					block.style.removeProperty("--timeline-content-max");
					return;
				}
				const tallest = aboveContents.reduce((height, el) => Math.max(height, el.offsetHeight), 0);
				block.style.setProperty("--timeline-content-max", `${Math.ceil(tallest)}px`);
			};

			alternating.addEventListener("change", setHalf);
			// Les hauteurs mesurées avant le swap des webfonts sont fausses
			if (document.fonts) document.fonts.ready.then(setHalf);

			// L'axe n'est plein qu'à partir du premier point, l'amorce restant en pointillés
			// (cf. _block-timeline.scss) : on mesure la distance entre le bord de la piste et
			// le premier slide, soit la place prise par la flèche retour plus le retrait
			// d'entrée du slider.
			const setLead = (swiper) => {
				const offset = swiper.params.slidesOffsetBefore || 0;
				block.style.setProperty("--timeline-lead", `${swiperEl.offsetLeft + offset}px`);
			};

			// isLocked : toutes les étapes tiennent dans la largeur, la frise est donc
			// entièrement parcourue et la ligne remplie.
			// Le progress sort de [0, 1] quand on tire la frise hors de ses bornes : sans borne,
			// le scaleX devient négatif (l'axe plein file à gauche de son cadre) ou supérieur
			// à 1 (il dépasse à droite).
			const setProgress = (swiper) => {
				if (progressEl) {
					const progress = swiper.isLocked ? 1 : Math.min(1, Math.max(0, swiper.progress));
					progressEl.style.transform = `scaleX(${progress})`;
				}
				// Pilote le fondu latéral (cf. _block-timeline.scss) : pas de fondu du côté où
				// la frise est en butée.
				block.classList.toggle("is-start", swiper.isBeginning);
				block.classList.toggle("is-end", swiper.isEnd);
			};

			new Swiper(swiperEl, {
				slidesPerView: 1.25,
				spaceBetween: 24,
				// Retrait avant la première étape : l'amorce de l'axe respire. Il reste court
				// au doigt, sans quoi il repousse hors de l'écran l'amorce de l'étape suivante,
				// seul indice qu'il reste de la frise à parcourir.
				slidesOffsetBefore: 12,
				grabCursor: true,
				freeMode: {
					enabled: true,
					sticky: false,
					momentumBounce: false,
				},
				navigation: {
					prevEl: prevEl,
					nextEl: nextEl,
				},
				a11y: {
					prevSlideMessage: (prevEl && prevEl.getAttribute("aria-label")) || "Étape précédente",
					nextSlideMessage: (nextEl && nextEl.getAttribute("aria-label")) || "Étape suivante",
				},
				// Étapes resserrées dès md : l'alternance haut / bas de l'axe évite que deux
				// textes voisins se chevauchent, les dates peuvent donc être rapprochées.
				breakpoints: {
					768: { slidesPerView: 2.6, spaceBetween: 32, slidesOffsetBefore: 40 },
					1200: { slidesPerView: 3.6, spaceBetween: 40, slidesOffsetBefore: 48 },
				},
				on: {
					afterInit: (swiper) => {
						setHalf();
						setLead(swiper);
						setProgress(swiper);
					},
					progress: setProgress,
					resize: (swiper) => {
						setHalf();
						setLead(swiper);
						setProgress(swiper);
					},
					// Durée donnée par Swiper : 0 pendant le drag, la vitesse du slider sur
					// un clic de flèche — la ligne reste ainsi calée sur les étapes.
					setTransition: (swiper, duration) => {
						if (progressEl) progressEl.style.transitionDuration = `${duration}ms`;
					},
				},
			});
		});
	}
}
